import * as dom from './../../modules/wpstg-dom-utils';
import WPStagingCommon from '../../wpstg';
import WPStagingBackup from './wpstg-backup';
import WPStagingBackupUpload from './wpstg-backup-upload.js';

export default class WpstgBackupUploadUrl {
  constructor(wpstgObject = wpstg) {
    this.wpstgObject = wpstgObject;
    this.currentBackupName = '';
    this.uploadProcessPercentage = 0;
    this.isUploadCompleted = false;
    this.isUploadCanceled = false;
    this.isProcessStarted = false;
    this.isWpstgBackupFile = false;
    this.uploadBackupFromUrl = this.uploadBackupFromUrl.bind(this);
  }

  /**
   * Start the upload process by displaying the progress bar
   *
   * @return {void}
   */
  startUploadProcess() {
    dom.hide('.wpstg--swal2-container #wpstg-upload-select');
    dom.show('.wpstg--swal2-container #wpstg-upload-progress');
    dom.show('.wpstg--swal2-container .wpstg--modal--backup--upload--content .wpstg-linear-loader');
    dom.qs('.wpstg--swal2-container .wpstg--modal--upload--process').style.display = 'flex';
  }

  /**
   * Stop the upload process
   *
   * @return {void}
   */
  stopUploadProcess() {
    setTimeout(() => {
      this.currentBackupName = '';
      this.uploadProcessPercentage = 0;
      this.isUploadCompleted = false;
      this.isUploadCanceled = false;
      this.isProcessStarted = false;
    }, 2000);
  }

  /**
   * Update the upload progress bar and percentage
   *
   * @return {void}
   */
  updateUploadProcess() {
    dom.qs('.wpstg--swal2-container .wpstg--modal--upload--progress').style.width = parseInt(this.uploadProcessPercentage) + '%';
    dom.qs('.wpstg--swal2-container .wpstg--modal--upload--progress--title > span').textContent = this.uploadProcessPercentage + '%';
  }

  hasWpstgExtension() {
    return this.currentBackupName.endsWith('.wpstg');
  }

  /**
   * Extract the backup name from a given URL.
   * @param {string} backupUrl
   *
   * @return {void}
   */
  extractBackupNameFromUrl(backupUrl) {
    try {
      const url = new URL(backupUrl);
      this.currentBackupName = url.pathname.split('/').pop();
    } catch (error) {
      dom.notify('error', error.message);
    }
  }

  /**
   * Handle a successful response from the server.
   * @param {object} response - The response data from the server.
   *
   * @return {void}
   */
  handleSuccessResponse(response) {
    WPStagingCommon.closeSwalModal();

    const message = response.message ? response.message : '';

    if (response.success) {
      dom.notify('success', (message ? message : 'Success'));
    } else {
      WPStagingCommon.showErrorModal((message ? message : 'Invalid request data'));
    }

    WPStagingCommon.showSuccessModal(dom.qs('#wpstg--modal--backup--upload').getAttribute('data-uploadSuccessMessage', 'Upload finished'));
    dom.setLoadingPlaceholder().then(() => dom.loadingPlaceholder());
    WPStagingBackup.fetchListing();
    this.stopUploadProcess();
  }

  /**
   * Handle an in progress response from the server.
   * @param {object} response - The response data from the server.
   *
   * @return {void}
   */
  handleInProgressResponse(response) {
    this.uploadProcessPercentage = Math.round((response.startByte / response.fileSize) * 100, 1);
    if (this.uploadProcessPercentage > 100) {
      this.uploadProcessPercentage = 100;
    }

    this.updateUploadProcess();
    this.uploadBackupFromUrl(response.fileSize, response.startByte);
  }

  /**
   * Handle an error response from the server and ask whether to resume or cancel the download.
   * @param {number} fileSize - The size of the file.
   * @param {number} startByte - The starting byte for the upload.
   *
   * @return {void}
   */
  handleErrorResponse(fileSize, startByte) {
    if (this.isProcessStarted === false) {
      return;
    }

    const resumeDownload = confirm('An error occurred during the download. Do you want to resume the download?');
    if (resumeDownload) {
      this.uploadBackupFromUrl(fileSize, startByte);
      return;
    }

    WPStagingCommon.closeSwalModal();
    dom.setLoadingPlaceholder().then(() => dom.loadingPlaceholder());
    WPStagingBackup.fetchListing();
    this.stopUploadProcess();
  }

  /**
   * Adds an event listener when cancel button clicked during uploading process
   *
   * @return {void}
   */
  addEvent() {
    const _btnCancel = WPStagingCommon.getSwalContainer().getElementsByClassName('wpstg--swal2-cancel wpstg--btn--cancel')[0];
    const btnCancel = _btnCancel.cloneNode(true);
    _btnCancel.parentNode.replaceChild(btnCancel, _btnCancel);
    btnCancel.addEventListener('click', () => this.handleCancelProcess());
  }

  /**
   * Handles cancel upload process
   *
   * @return {void}
   */
  handleCancelProcess() {
    const resumeDownload = confirm('Do you want to abort the upload?');
    this.isUploadCanceled = true;
    if (resumeDownload) {
      WPStagingCommon.closeSwalModal();
      WPStagingBackupUpload.deleteUnfinishedUploads();
      dom.setLoadingPlaceholder().then(() => dom.loadingPlaceholder());
      WPStagingBackup.fetchListing();
      this.stopUploadProcess();
      return;
    }

    this.isUploadCanceled = false;
  }

  /**
   * Upload a backup from a URL.
   * @param {number} fileSize - The size of the file.
   * @param {number} startByte - The starting byte for the upload.
   *
   * @return {void}
   */
  async uploadBackupFromUrl(fileSize = 0, startByte = 0) {
    if (this.isUploadCanceled) {
      return false;
    }

    const container = WPStagingCommon.getSwalContainer();
    const backupUrl = container.querySelector('input[name="wpstg-backup-url"]').value;
    if (dom.checkIfElementNullOrUndefinedOrEmpty(backupUrl)) {
      dom.notify('error', 'Backup file URL is empty');
      return false;
    }

    if (this.isProcessStarted === false) {
      await this.extractBackupNameFromUrl(backupUrl);
      this.isWpstgBackupFile = await this.hasWpstgExtension();
    }

    if (this.currentBackupName === '') {
      dom.notify('error', 'Please enter a valid backup file url.');
      return false;
    }

    if (!this.isWpstgBackupFile) {
      dom.notify('error', 'Please enter correct backup file url');
      return false;
    }

    if (this.isProcessStarted === false) {
      this.isProcessStarted = true;
      this.startUploadProcess();
      this.addEvent();
    }

    fetch(this.wpstgObject.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: new URLSearchParams({
        action: 'wpstg-backups-download-url',
        accessToken: this.wpstgObject.accessToken,
        nonce: this.wpstgObject.nonce,
        backupUrl: backupUrl,
        fileSize: fileSize,
        startByte: startByte,
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }

      return Promise.reject(response);
    }).then((data) => {
      this.isUploadCompleted = data.complete;
      if (data.complete === false && data.success) {
        this.handleInProgressResponse(data);
        return;
      }

      this.handleSuccessResponse(data);
    }).catch((error) => {
      if (this.isUploadCompleted === false) {
        this.handleErrorResponse(fileSize, startByte);
      }
    });
  }
}
